import React, { useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from '../../../app/LanguageContext';
import { formatCurrency } from '../../../app/currency';
import { computeConsignmentFinancials } from './consignmentFinancials';
import ConfirmDialog from '../../../modules/core/public/ConfirmDialog';
import { removeConsignmentItem } from './consignmentApi';

export default function ConsignmentItemsTable({
  consignmentId,
  items,
  status,
  onEditItem,
  onRefresh,
  onError,
}) {
  const { translate } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteObservation, setDeleteObservation] = useState('');

  const isActive = status === 'ACTIVE';
  const isEditable = status === 'PREPARATION' || status === 'ACTIVE';

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    if (isActive && !deleteObservation.trim()) {
      onError(translate('consignment.observation_required'));
      return;
    }
    try {
      await removeConsignmentItem(consignmentId, confirmDelete.id, {
        observation: deleteObservation.trim(),
      });
      setConfirmDelete(null);
      setDeleteObservation('');
      onRefresh();
    } catch (e) {
      onError(e.message);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" mb={1}>
        {translate('consignment.items_title')}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{translate('consignment.item_product')}</TableCell>
              <TableCell align="right">{translate('consignment.item_sale_price')}</TableCell>
              <TableCell align="right">{translate('consignment.store_share')}</TableCell>
              <TableCell align="right">{translate('consignment.net_value_company')}</TableCell>
              <TableCell align="right">{translate('consignment.item_qty_sent')}</TableCell>
              <TableCell align="right">{translate('consignment.item_qty_sold')}</TableCell>
              <TableCell align="right">{translate('consignment.item_balance')}</TableCell>
              {isEditable && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {(!items || items.length === 0) && (
              <TableRow>
                <TableCell colSpan={isEditable ? 8 : 7} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    {translate('consignment.no_items')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {(items || []).map((item) => {
              const balance = item.qty_sent - item.qty_sold - item.qty_returned;
              const rowFinancials = computeConsignmentFinancials(item);
              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{item.product_name}</Typography>
                    {item.product_sku_display && (
                      <Typography variant="caption" color="text.secondary">{item.product_sku_display}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{formatCurrency(item.sale_price)}</TableCell>
                  <TableCell align="right">{formatCurrency(rowFinancials.storeShare)}</TableCell>
                  <TableCell align="right">{formatCurrency(rowFinancials.net)}</TableCell>
                  <TableCell align="right">{item.qty_sent}</TableCell>
                  <TableCell align="right">{item.qty_sold}</TableCell>
                  <TableCell align="right">
                    <Chip label={balance} size="small" color={balance > 0 ? 'success' : 'default'} />
                  </TableCell>
                  {isEditable && (
                    <TableCell align="right">
                      <Tooltip title={translate('common.edit')}>
                        <IconButton size="small" onClick={() => onEditItem(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {item.qty_sold === 0 && (
                        <Tooltip title={translate('common.delete')}>
                          <IconButton size="small" onClick={() => setConfirmDelete(item)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={!!confirmDelete}
        onCancel={() => {
          setConfirmDelete(null);
          setDeleteObservation('');
        }}
        onConfirm={handleConfirmDelete}
        title={translate('consignment.delete_item_title')}
        message={translate('consignment.delete_item_message')}
        detail={isActive ? (
          <TextField
            label={translate('consignment.change_observation')}
            value={deleteObservation}
            onChange={(e) => setDeleteObservation(e.target.value)}
            fullWidth
            multiline
            rows={2}
            size="small"
            sx={{ mt: 2 }}
            required
          />
        ) : null}
      />
    </Box>
  );
}
